// Rich client copy — the frontend half of backend services/rich_copy.py.
// The copy editor stores sanitized HTML (bolds/headings carry meaning for a
// landing-page build); every render of stored copy re-sanitizes here first,
// so stored bytes are still treated as untrusted at display time. Keep the
// allowlist in lockstep with the backend's.
const ALLOWED_TAGS = new Set([
  'P', 'BR', 'DIV', 'SPAN', 'BLOCKQUOTE',
  'B', 'STRONG', 'I', 'EM', 'U', 'S',
  'H1', 'H2', 'H3', 'H4',
  'UL', 'OL', 'LI',
  'A',
]);
const DROP_CONTENT_TAGS = new Set(['SCRIPT', 'STYLE', 'HEAD', 'TITLE', 'META', 'LINK', 'IFRAME', 'OBJECT', 'EMBED']);
const BLOCK_TAGS = new Set(['P', 'DIV', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'UL', 'OL', 'LI', 'BR', 'TR', 'TABLE']);
const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

function escapeHtml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function sanitizeNode(node, out) {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === 3) { out.push(escapeHtml(child.nodeValue || '')); continue; }
    if (child.nodeType !== 1) continue;
    const tag = child.tagName;
    if (DROP_CONTENT_TAGS.has(tag)) continue;
    if (!ALLOWED_TAGS.has(tag)) { sanitizeNode(child, out); continue; }
    if (tag === 'BR') { out.push('<br>'); continue; }
    if (tag === 'A') {
      const href = (child.getAttribute('href') || '').trim();
      if (SAFE_HREF.test(href)) {
        out.push(`<a href="${escapeHtml(href).replace(/"/g, '&quot;')}">`);
        sanitizeNode(child, out);
        out.push('</a>');
      } else {
        out.push('<span>');
        sanitizeNode(child, out);
        out.push('</span>');
      }
      continue;
    }
    const t = tag.toLowerCase();
    out.push(`<${t}>`);
    sanitizeNode(child, out);
    out.push(`</${t}>`);
  }
}

// Allowlisted HTML from any HTML string — all attributes stripped (a[href]
// http(s)/mailto kept), unknown tags removed with their text kept,
// script/style dropped wholesale. '' when nothing textual survives.
export function sanitizeCopyHtml(raw) {
  const s = (raw || '').trim();
  if (!s) return '';
  const doc = new DOMParser().parseFromString(s, 'text/html');
  const out = [];
  sanitizeNode(doc.body, out);
  const html = out.join('').trim();
  return htmlToText(html).trim() ? html : '';
}

// Plain text from HTML: block tags become line breaks. Mirrors the backend's
// html_to_text so "is there real copy here" agrees on both sides.
export function htmlToText(raw) {
  const s = (raw || '').trim();
  if (!s) return '';
  const doc = new DOMParser().parseFromString(s, 'text/html');
  const parts = [];
  const walk = (node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === 3) { parts.push(child.nodeValue || ''); continue; }
      if (child.nodeType !== 1) continue;
      if (DROP_CONTENT_TAGS.has(child.tagName)) continue;
      const isBlock = BLOCK_TAGS.has(child.tagName);
      if (isBlock) parts.push('\n');
      walk(child);
      if (isBlock) parts.push('\n');
    }
  };
  walk(doc.body);
  return parts.join('')
    .split('\n').map(l => l.trim()).join('\n')
    .replace(/\n{3,}/g, '\n\n').trim();
}

// Plain text -> minimal HTML (escape + line structure), for editing a copy
// blob saved before the rich editor existed.
export function textToHtml(text) {
  const s = (text || '').trim();
  if (!s) return '';
  return s.split(/\n{2,}/)
    .map(para => `<p>${escapeHtml(para).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

// What a paste into the copy editor inserts (the 2026-10-28 walkthrough:
// opt-in copy pasted from a Word doc kept the source's widths and collapsed
// into a narrow column). The clipboard's HTML is sanitized on the way in —
// formatting kept, styles and widths dropped; plain text keeps its lines.
export function pastedHtml({ html, text } = {}) {
  const clean = html ? sanitizeCopyHtml(html) : '';
  return clean || textToHtml(text);
}
